import Vue from 'vue'

import VueI18n from 'vue-i18n'
import axios from 'axios'
import toastr from 'toastr'

import App from './App.vue'
import router from './router'
import {mimeType} from "./mime_type"
import Store from "./store"
import ListStore from "./listStore"
import StatStore from "./statStore"
import OfficeStore from "./officeStore"

import Application from "./layouts/Application"
import Public from "./layouts/Public"
import Outlook from "./layouts/OutlookLayout"
import featureToggle from "@/config/feature_toggle.json";

document.addEventListener('DOMContentLoaded', () => {
  Vue.directive('click-outside', {
    bind(el, binding, vnode) {
      el.clickOutsideEvent = (event) => {
        if (el !== event.target && !el.contains(event.target) && document.body.contains(event.target))
          vnode.context[binding.expression](event);
      }
      document.body.addEventListener('click', el.clickOutsideEvent)
    },
    unbind(el) {
      document.body.removeEventListener('click', el.clickOutsideEvent)
    },
  })

  Vue.directive('scroll', {
    bind(el, binding, vnode) {
      el.addEventListener('scroll', (ev) => vnode.context[binding.expression](ev))
    }
  })

  Vue.config.productionTip = true
  Vue.component('application', Application)
  Vue.component('public', Public)
  Vue.component('outlook', Outlook)

  // Initialize VueI18n without any language loaded
  Vue.use(VueI18n)
  const i18n = new VueI18n({
    locale: 'current',
    messages: {}
  });

  toastr.options = {
    "closeButton": true,
    "debug": false,
    "progressBar": false,
    "newestOnTop": true,
    "positionClass": "toast-top-right",
    "onclick": null,
    "showDuration": "1000",
    "hideDuration": "1000",
    "timeOut": "3000",
    "extendedTimeOut": "1000",
    "showMethod": "fadeIn",
    "hideMethod": "fadeOut"
  }
  toastr.alert = toastr.error
  toastr.notice = toastr.success
  Vue.prototype.$toastr = toastr

  let axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_URL + process.env.VUE_APP_API_PATH,
    withCredentials: true,
  })
  axiosInstance.defaults.headers.common = {}
  axiosInstance.defaults.headers.common.accept = 'application/json'
  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      switch (error.response.status) {
        case 401:
          if(error.response.config.url.match(/msoffice/)){
            router.replace({name: 'msoffice_login'}).catch(error => {
              if (error.name !== 'NavigationDuplicated') { throw error }
            })
          }
          else
            router.replace({name: 'login'})
          break
        case 410:
          router.replace(error.response.data.router_params)
          break
        case 422:
          toastr.error(Object.entries(error.response.data.errors).map(([k, vs]) => {
            return vs.map(v => `${k.replace('_', ' ')} ${v}`).join('<br />')
          }).join('<br />'))
          break
        case 404:
          toastr.alert(i18n.t('flash_messages.resource_gone'))
          break
        case 403:
          toastr.alert(i18n.t('flash_messages.resource_protected'))
          break
        default:
          toastr.alert(i18n.t('flash_messages.generic_error'))
          break
      }
      return Promise.reject(error)
    }
  )

  const store = new Store(axiosInstance)
  const listStore = new ListStore(axiosInstance)
  const statStore = new StatStore(axiosInstance)
  const officeStore = new OfficeStore(axiosInstance)

  Vue.mixin({
    data() {
      return {
        store: store,
        listStore: listStore,
        statStore: statStore,
        axios: axiosInstance,
        officeStore: officeStore
      }
    },
    created() {
      this.toggleData = JSON.parse(JSON.stringify(featureToggle))
    },
    watch: {
      translations() {
        i18n.setLocaleMessage('current', this.translations)
      }
    },
    computed: {
      lists() {
        return this.listStore.state
      },
      listMeta() {
        return this.lists.meta || {}
      },
      stats() {
        return this.statStore.state
      },
      state() {
        return this.store.state
      },
      officeState() {
        return this.officeStore.state
      },
      meta() {
        return this.state.meta || {}
      },
      query() {
        return this.$route ? this.$route.query : {}
      },
      currentUser() {
        return this.state.currentUser || {}
      },
      locale() {
        return this.query.locale || this.currentUser.locale || 'en'
      },
      translations() {
        return this.store.translations[this.locale] || {}
      },
      currentOrg(){
        return this.state.organization || {}
      },
      userDocumentTypeInfo() {
        const defaults = this.currentUser.defaults || {}
        return defaults.document_type_view || {}
      },
      userUpdatedAtInfo() {
        const defaults = this.currentUser.defaults || {}
        return defaults.updated_at_view || {}
      },
      userProductInfo() {
        const defaults = this.currentUser.defaults || {}
        return defaults.product_view || {}
      },
      userDocumentTypeTitle() {
        return this.userDocumentTypeInfo.title ? this.userDocumentTypeInfo.title : this.$t('sidebar.doc_types')
      },
      userUpdatedAtTitle() {
        return this.userUpdatedAtInfo.title ? this.userUpdatedAtInfo.title : this.$t('public.updated_at')
      },
      userDocumentTypeName() {
        return this.userDocumentTypeInfo.name || this.$t('document_types.index.type_name')
      },
      userProductTitle() {
        return this.userProductInfo.title ? this.userProductInfo.title : this.$t('sidebar.products')
      },
      userProductName() {
        return this.userProductInfo.name || this.$t('products.product_name')
      },
      userProductCode() {
        return this.userProductInfo.code || this.$t('products.product_code')
      },
      unverifiedUser(){
        return this.currentUser.status === "unverified"
      },
      userDefaults() {
        return this.currentUser.defaults || {}
      },
      hasFilters() {
        return this.$route.fullPath.indexOf('filters') >= 0
      },
      expStatusTranslations() {
        return {
          current: this.$t('folders_list.up_to_date'),
          soon: this.$t('folders_list.expires_soon'),
          expired: this.$t('folders_list.file_expired'),
          no_expiration: this.$t('folders_list.no_exp_date'),
        }
      },
      baseUrl(){
        return process.env.VUE_APP_API_URL
      },
      baseApiUrl(){
        return this.baseUrl + process.env.VUE_APP_API_PATH
      }
    },
    methods: {
      _debounce(fn, delay) {
        let timeoutID = null

        return () => {
          clearTimeout(timeoutID)
          let args = arguments
          let that = this
          timeoutID = setTimeout(() => fn.apply(that, args), delay)
        }
      },
      toggles(feature_name){
        if(process.env.NODE_ENV !== 'production'){
          return true
        }

        const feature = this.toggleData[feature_name]
        if(feature !== null) {
          return (feature.organization_ids.includes(this.currentOrg.id) ||
                  feature.user_ids.includes(this.currentUser.id))
        }else{
          return false
        }
      },
      copyToClipboard(event, input_selector) {
        let wrapper = $(event.target).parents(".copy-to-clipboard-wrapper")
        let $temp = $("<input>")
        wrapper.prepend($temp)
        $temp.val(wrapper.find(input_selector)[0].value).select()
        document.execCommand("copy")
        $temp.remove()
        this.$toastr.success(this.$t('folder_show.settings.copy_clipboard')).css("width", "300px")
      },
      formatDate(date, opts) {
        let month = this.locale === "de" ? 'numeric' : 'short'
        const options = Object.assign({day: 'numeric',  month: month, year: 'numeric'}, opts)

        if (date) {
          return new Date(date).toLocaleDateString(this.locale, options)
        }
        return null
      },
      formatTime(date, opts) {
        const options = Object.assign({ timeStyle: 'short' }, opts)
        return new Date(date).toLocaleTimeString(this.locale, options)
      },
      mimeToSrc(mime) {
        const m = mimeType(mime)
        return require(`./assets/items/${m}.svg`)
      },
      iconType(model) {
        if (model.source_type === 'Record') {
          const recordMimeType = mimeType(model.mime_type)
          if (recordMimeType === 'pdf') {
            return {alt: 'File', src: require('./assets/search/pdf-icon.svg')}
          } else if (recordMimeType === 'img') {
            return {alt: 'File', src: require('./assets/search/img-icon.svg')}

          } else if (recordMimeType === 'xls') {
            return {alt: 'File', src: require('./assets/search/xls-icon.svg')}

          } else if (recordMimeType === 'ppt') {
            return {alt: 'File', src: require('./assets/search/ppt-icon.svg')}

          } else if (recordMimeType === 'docx') {
            return {alt: 'File', src: require('./assets/search/docx-icon.svg')}
          } else {
            return {alt: 'File', src: require('./assets/search/docx-icon.svg')}
          }
        } else if (model.source_type === 'Folder' || model.source_type === 'SharedFolder') {
          return {alt: 'Folder', src: require('./assets/search/folders.svg')}
        } else if (model.source_type === 'Product') {
          return {alt: 'Product', src: require('./assets/search/products.svg')}
        } else if (model.source_type === 'Contact') {
          return {alt: 'Contact', src: require('./assets/search/contacts.svg')}
        } else if (model.source_type === 'DocumentType') {
          return {alt: 'DocumentType', src: require('./assets/search/file-icon.svg')}
        } else if (model.source_type === 'Tag') {
          return {alt: 'Tags', src: require('./assets/search/tags.svg')}
        } else if (model.source_type === 'DocumentRequest') {
          return {alt: 'Message', src: require('./assets/search/sent-mail.svg')}
        } else if (model.source_type === 'DocumentResponse') {
          return {alt: 'Message', src: require('./assets/search/incoming-mail.svg')}

        } else if(model.source_type === 'SharedRecord') {
          return {alt: 'File', src: require('./assets/search/file-shared.svg')}
        } else {
          return {alt: 'File', src: require('./assets/search/file-icon.svg')}
        }
      },
      folderTemplate(f) {
        return f.contact_id ? `${f.name} - (${f.contact.name})` : `${f.name}`
      },
      contactTemplate(c) {
        const company = c.company ? `${this.$t('folders_list.from')} ${c.company}` : ''
        return c.email ? `${c.name} ${company} - ${c.email}` : `${c.name} ${company} - ${this.$t('folders_list.no_email')}`
      },
      redirectBack() {
        this.$router.replace(this.$router.referer)
      },
      addToQuery(params) {
        return Object.assign({}, this.$route, { query: Object.assign({}, this.query, params)})
      },
      sortBy(col) {
        let s = this.query['filters[s]'] || ''

        if (Array.isArray(s))
          s = s.join(',')

        if (s.indexOf(col) < 0)
          return col + ' asc'

        return s.indexOf('asc') >= 0 ? col + ' desc' : col + ' asc'
      },
      classSortDirection(col) {
        let s = this.query['filters[s]'] || ''

        if (Array.isArray(s))
          s = s.join(',')

        if (s.indexOf(col) < 0 || (s.indexOf('asc') < 0 && s.indexOf('desc') < 0))
          return ['fa', 'fa-sort']
        else if (s.indexOf('asc') >= 0)
          return ['fa', 'fa-sort-up']
        else
          return ['fa', 'fa-sort-down']
      },
      previewUrl(location){
        if(!location)
            return null

        const url = new URL(location) || ''
        const pathname = this.cleanPath(url.pathname) || ''

        return [this.baseUrl, 'preview', pathname].join('/')
      },
      cleanPath(path){
        // remove first and last slashes
        return path.replace(/^\/|\/$/g, '')
      },
      statusClass(status, type='text'){
        switch (status.toLowerCase()) {
          case 'pending':
            return type+'-warning'
          case 'delivered':
            return type+'-success'
          case 'opened':
            return type+'-primary'
          case 'failed':
            return type+'-danger'
          case 'active':
            return type+'-success'
          case 'inactive':
            return type+'-secondary'
          default:
            return type+'-secondary'
        }
      },
      statusText(key) {
        return {
          'pending': this.$t('communication.status_message.pending'),
          'failed': this.$t('communication.status_message.failed'),
          'delivered': this.$t('communication.status_message.delivered'),
          'opened': this.$t('communication.status_message.opened'),
          'active': this.$t('account_settings.company.table.active_user_status'),
          'inactive': this.$t('account_settings.company.table.inactive_user_status'),
        }[key]
      },
    },
    filters: {
      makeItRed(word, query) {
        const check = new RegExp(query, "gi")
        return word.toString().replace(check, (match) => ('<span>' + match + '</span>'))
      }
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        if (vm.$router) {
          vm.$router.referer = from;
        }
      });
    },
  })

  router.beforeEach((to, from, next) => {
    // bug in vuejs unfortunately sometimes does not match url and
    // redirects to dashboard. This workaround fix the correct route name
    // for addin which shouldn't open dashboard
    if (to.matched.length > 0 && to.name === 'home'){
      if (to.fullPath.match(/msoffice\/login/))
        return next({ name: 'msoffice_login', query: to.query })

      if (to.fullPath.match(/msoffice\/outlook\/reader/))
        return next({ name: 'outlook_taskpane_reader', query: to.query })

      if (to.fullPath.match(/msoffice\/outlook\/writer/))
        return next({ name: 'outlook_taskpane_writer', query: to.query })

      if (to.fullPath.match(/msoffice/))
        return next({ name: 'msoffice_home', query: to.query })
    }
    if(to.matched.length > 0 && to.name === 'login'){
      if (from.fullPath.match(/msoffice\/outlook/)){
        toastr.alert(i18n.t('flash_messages.resource_protected'))
        return next({ name: 'msoffice_home', query: to.query })
      }
    }

    if (to.meta.outlook)
      return next()

    store.getCurrentUser().then((data) => {
      if (to.meta.public || data.user) {
        next()
      } else {
        next('/login')
      }
    })
  })

  new Vue({
    router,
    i18n,
    render: h => h(App)
  }).$mount('#app')
})
